
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UseOptimizedExploreDataProps {
  recipes: any[];
  maxRecipes?: number;
}

export const useOptimizedExploreData = ({ recipes, maxRecipes = 80 }: UseOptimizedExploreDataProps) => {
  const [loadingStates, setLoadingStates] = useState({
    featured: true,
    new: true,
    popular: true,
    mostLiked: true
  });

  // Optimized recipe likes query with timeout and error handling
  const { data: recipeLikeCounts = {}, isLoading: isLoadingLikes, error: likesError } = useQuery({
    queryKey: ['recipe-likes-batch-optimized'],
    queryFn: async () => {
      console.log('🔄 Fetching recipe likes with timeout...');
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout fetching recipe likes')), 10000)
      );
      
      const queryPromise = supabase.rpc('get_recipe_like_counts');
      
      try {
        const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;
        
        if (error) {
          console.error('❌ Error fetching recipe likes:', error);
          return {};
        }
        
        const likesMap: Record<string, number> = {};
        if (data) {
          data.forEach((item) => {
            likesMap[item.recipe_id] = Number(item.count);
          });
          console.log(`✅ Loaded ${data.length} recipe like counts`);
        }
        
        return likesMap;
      } catch (error) {
        console.error('⚠️ Recipe likes query timeout or error:', error);
        return {};
      }
    },
    staleTime: 30000,
    retry: 1,
  });

  // Limit recipes to improve performance - memoized with stable dependencies
  const limitedRecipes = useMemo(() => {
    if (!recipes || recipes.length === 0) return [];
    
    try {
      console.log(`📊 Limiting recipes from ${recipes.length} to ${maxRecipes}`);
      
      // Sort by nutriscore and take the best ones first
      const sortedRecipes = [...recipes]
        .sort((a, b) => (b.nutriscore || 0) - (a.nutriscore || 0))
        .slice(0, maxRecipes);
      
      return sortedRecipes;
    } catch (error) {
      console.error('❌ Error limiting recipes:', error);
      return recipes.slice(0, maxRecipes); // Fallback
    }
  }, [recipes, maxRecipes]);

  // Progressive calculation of featured recipes - stable dependencies
  const featuredRecipes = useMemo(() => {
    if (!limitedRecipes.length) return [];
    
    console.log('🎯 Calculating featured recipes...');
    setLoadingStates(prev => ({ ...prev, featured: true }));
    
    try {
      const featured = [...limitedRecipes]
        .sort(() => 0.5 - Math.random())
        .slice(0, 8);
      
      setLoadingStates(prev => ({ ...prev, featured: false }));
      console.log('✅ Featured recipes calculated');
      return featured;
    } catch (error) {
      console.error('❌ Error calculating featured recipes:', error);
      setLoadingStates(prev => ({ ...prev, featured: false }));
      return limitedRecipes.slice(0, 8);
    }
  }, [limitedRecipes]);

  // Progressive calculation of new recipes - stable dependencies
  const newRecipes = useMemo(() => {
    if (!limitedRecipes.length) return [];
    
    console.log('🆕 Calculating new recipes...');
    setLoadingStates(prev => ({ ...prev, new: true }));
    
    try {
      const newOnes = [...limitedRecipes]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 8);
      
      setLoadingStates(prev => ({ ...prev, new: false }));
      console.log('✅ New recipes calculated');
      return newOnes;
    } catch (error) {
      console.error('❌ Error calculating new recipes:', error);
      setLoadingStates(prev => ({ ...prev, new: false }));
      return limitedRecipes.slice(0, 8);
    }
  }, [limitedRecipes]);

  // Progressive calculation of popular recipes - stable dependencies
  const popularRecipes = useMemo(() => {
    if (!limitedRecipes.length) return [];
    
    console.log('⭐ Calculating popular recipes...');
    setLoadingStates(prev => ({ ...prev, popular: true }));
    
    try {
      const popular = [...limitedRecipes]
        .sort((a, b) => {
          const nutrscoreDiff = (b.nutriscore || 0) - (a.nutriscore || 0);
          if (nutrscoreDiff === 0) {
            return (b.protein || 0) - (a.protein || 0);
          }
          return nutrscoreDiff;
        })
        .slice(0, 8);
      
      setLoadingStates(prev => ({ ...prev, popular: false }));
      console.log('✅ Popular recipes calculated');
      return popular;
    } catch (error) {
      console.error('❌ Error calculating popular recipes:', error);
      setLoadingStates(prev => ({ ...prev, popular: false }));
      return limitedRecipes.slice(0, 8);
    }
  }, [limitedRecipes]);

  // Progressive calculation of most liked recipes - stable dependencies
  const mostLikedRecipes = useMemo(() => {
    if (!limitedRecipes.length || isLoadingLikes) return [];
    
    console.log('❤️ Calculating most liked recipes...');
    setLoadingStates(prev => ({ ...prev, mostLiked: true }));
    
    try {
      const recipesWithLikes = limitedRecipes.map(recipe => ({
        ...recipe,
        likeCount: recipeLikeCounts[recipe.id] || 0
      }));

      const sortedRecipes = [...recipesWithLikes]
        .sort((a, b) => {
          const likeDiff = (b.likeCount || 0) - (a.likeCount || 0);
          if (likeDiff === 0) {
            return (b.nutriscore || 0) - (a.nutriscore || 0);
          }
          return likeDiff;
        });

      const topRecipes = sortedRecipes.slice(0, 8);
      
      setLoadingStates(prev => ({ ...prev, mostLiked: false }));
      console.log('✅ Most liked recipes calculated');
      
      return topRecipes;
    } catch (error) {
      console.error('❌ Error calculating most liked recipes:', error);
      setLoadingStates(prev => ({ ...prev, mostLiked: false }));
      return limitedRecipes.slice(0, 8);
    }
  }, [limitedRecipes, recipeLikeCounts, isLoadingLikes]);

  // Log errors only when they occur
  useEffect(() => {
    if (likesError) {
      console.error('Recipe likes query error:', likesError);
    }
  }, [likesError]);

  return {
    featuredRecipes,
    newRecipes,
    popularRecipes,
    mostLikedRecipes,
    loadingStates,
    isLoadingLikes,
    totalRecipesProcessed: limitedRecipes.length,
    originalRecipesCount: recipes.length
  };
};
